import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Minus, Plus, HandHelping, ShoppingBag, Users, Footprints, Home as HomeIcon, PartyPopper, Package, User, CalendarDays } from "lucide-react";

export const WINDOWS = [["9 AM-12 PM", 9, "सुबह 9-12"], ["12-3 PM", 12, "दोपहर 12-3"], ["3-6 PM", 15, "दोपहर 3-6"], ["6-9 PM", 18, "शाम 6-9"]];
const ICONS = { "shopping-bag": ShoppingBag, users: Users, footprints: Footprints, home: HomeIcon, "party-popper": PartyPopper, package: Package, user: User };
const h12 = (h) => (h > 12 ? h - 12 : h);
const mer = (h) => (h >= 12 ? "PM" : "AM");
export const rangeLabel = (h, n) => (mer(h) === mer(h + n) ? `${h12(h)}-${h12(h + n)} ${mer(h)}` : `${h12(h)} ${mer(h)}-${h12(h + n)} ${mer(h + n)}`);
const ist = (date, h, m = 0) => new Date(`${date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00+05:30`).getTime();
const winOk = (date, h) => !!date && ist(date, h + 3) > Date.now();
const hourOk = (date, h) => !!date && ist(date, h, 30) > Date.now();
const dayStr = (add) => { const d = new Date(Date.now() + add * 864e5 + 5.5 * 36e5); return d.toISOString().slice(0, 10); };
const chip = (on, ok = true) => `rounded-2xl border text-sm font-bold transition-colors ${on ? "bg-[#121316] text-white border-[#121316]" : ok ? "bg-white border-[#E5E0D8] text-[#121316] hover:border-[#E05624]" : "bg-[#F3EFEA] border-[#E5E0D8] text-[#B5B2AC] cursor-not-allowed line-through"}`;

export function Step({ n, title, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5"><span className="h-7 w-7 rounded-full bg-[#E05624] text-white text-sm font-extrabold flex items-center justify-center">{n}</span><span className="font-display font-bold text-lg text-[#121316]">{title}</span></div>
      {children}
    </div>
  );
}

export function ServiceChips({ services, lang, value, onChange }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {services.map((s) => {
        const Icon = ICONS[s.icon] || HandHelping;
        return (
          <button type="button" key={s.id} data-testid={`svc-${s.id}`} onClick={() => onChange(s.id)} className={`${chip(value === s.id)} h-14 px-3 flex items-center gap-2 text-left`}>
            <Icon size={18} className={value === s.id ? "text-[#F0894F]" : "text-[#E05624]"} /> <span className="leading-tight">{s[lang].title}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ServiceScope({ svc, lang, t }) {
  if (!svc) { return null; }
  const c = svc[lang];
  return (
    <div data-testid="service-scope" className="rounded-2xl border border-[#E5E0D8] bg-white p-4 space-y-2 text-sm">
      <ul className="space-y-1 text-[#3A3D45]">
        {c.items.slice(0, 5).map((i) => <li key={i} className="flex gap-2"><span className="text-[#1B8A4B] font-bold">✓</span>{i}</li>)}
      </ul>
      <p data-testid="service-not-included" className="rounded-xl bg-[#FDECEC] text-[#A8322A] font-semibold px-3 py-2">
        ✕ {t("not_included")}: {c.no || t("no_cleaning")}
      </p>
    </div>
  );
}

export function DateChips({ t, value, onChange }) {
  const [other, setOther] = useState(false);
  const today = dayStr(0);
  const tom = dayStr(1);
  return (
    <div className="flex flex-wrap gap-2">
      <button type="button" data-testid="date-today" onClick={() => { setOther(false); onChange(today); }} className={`${chip(value === today)} h-12 px-5`}>{t("d_today")}</button>
      <button type="button" data-testid="date-tomorrow" onClick={() => { setOther(false); onChange(tom); }} className={`${chip(value === tom)} h-12 px-5`}>{t("d_tomorrow")}</button>
      <label className={`${chip(other || (value && value !== today && value !== tom))} h-12 px-4 inline-flex items-center gap-2 cursor-pointer`}>
        <CalendarDays size={16} />
        <input data-testid="booking-date" type="date" min={today} value={value && value !== today && value !== tom ? value : ""} onChange={(e) => { setOther(true); onChange(e.target.value); }} className="bg-transparent outline-none text-sm font-bold w-[8.5rem]" aria-label={t("d_other")} />
      </label>
    </div>
  );
}

export function HoursStepper({ t, lang, value, onChange }) {
  const lbl = lang === "hi" ? `${value} ${value > 1 ? "घंटे" : "घंटा"}` : `${value} hour${value > 1 ? "s" : ""}`;
  return (
    <div className="inline-flex items-center gap-3 rounded-full border border-[#E5E0D8] bg-white p-1.5">
      <button type="button" data-testid="hours-minus" disabled={value <= 1} onClick={() => onChange(value - 1)} className="h-11 w-11 rounded-full bg-[#F3EFEA] flex items-center justify-center disabled:opacity-40"><Minus size={18} /></button>
      <span data-testid="hours-value" className="min-w-[5.5rem] text-center font-display font-extrabold text-lg text-[#121316]">{lbl}</span>
      <button type="button" data-testid="hours-plus" disabled={value >= 8} onClick={() => onChange(value + 1)} className="h-11 w-11 rounded-full bg-[#121316] text-white flex items-center justify-center disabled:opacity-40"><Plus size={18} /></button>
    </div>
  );
}

export function TimePicker({ t, lang, date, hours, value, onChange }) {
  const sel = WINDOWS.find(([v, h]) => v === value || (value && value !== v && hourIn(value, h)));
  const [win, setWin] = useState(sel ? sel[1] : null);
  const [blocked, setBlocked] = useState([]);
  useEffect(() => {
    if (!date) { return; }
    api.get(`/slots/blocked?date=${date}`).then((r) => setBlocked(r.data.hours || [])).catch(() => setBlocked([]));
  }, [date]);
  const free = (h, n) => Array.from({ length: n }, (_, i) => h + i).every((x) => !blocked.includes(x));
  const winFree = (h) => [h, h + 1, h + 2].some((x) => !blocked.includes(x));
  const active = win ?? sel?.[1] ?? null;
  const starts = active == null ? [] : [active, active + 1, active + 2].filter((h) => h + hours <= 21 && !(h === active && hours === 3));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {WINDOWS.map(([v, h, hi]) => {
          const ok = winOk(date, h) && h + hours <= 21 && winFree(h);
          return <button type="button" key={v} data-testid={`slot-${h}`} disabled={!ok} onClick={() => { setWin(h); onChange(v); }} className={`${chip(active === h, ok)} h-12`}>{lang === "hi" ? hi : v}</button>;
        })}
      </div>
      {active != null && (
        <div data-testid="hour-picker" className="rounded-2xl bg-[#FFF1EC] border border-[#F6D9CD] p-3">
          <p className="text-xs font-bold text-[#8A3B17] mb-2">{t("t_exact")}</p>
          <div className="flex flex-wrap gap-2">
            {starts.map((h) => {
              const lbl = rangeLabel(h, hours);
              const ok = hourOk(date, h) && free(h, hours);
              return <button type="button" key={h} data-testid={`hour-${h}`} disabled={!ok} onClick={() => onChange(lbl)} className={`${chip(value === lbl, ok)} h-11 px-4`}>{lbl}</button>;
            })}
            <button type="button" data-testid="hour-any" onClick={() => onChange(WINDOWS.find((w) => w[1] === active)[0])} className={`${chip(value === WINDOWS.find((w) => w[1] === active)[0])} h-11 px-4`}>{t("t_any")}</button>
          </div>
        </div>
      )}
      <p className="text-xs text-[#5A5D65]">{date ? t("slot_soon") : t("slot_hint")}</p>
    </div>
  );
}

export function startHour(label) {
  const m = /^(\d{1,2})(?:\s*(AM|PM))?-\d{1,2}\s*(AM|PM)$/.exec(label || "");
  if (!m) { return null; }
  const h = Number(m[1]);
  const md = m[2] || m[3];
  return h === 12 ? 12 : h + (md === "PM" ? 12 : 0);
}

function hourIn(label, start) {
  const h = startHour(label);
  return h != null && h >= start && h < start + 3;
}
